// Global Instructions Rule Applied!
//
// Helpers for table list filters + sorting stored on `user_preferences_reports.data`
// (same pattern as Downloads). Used by Deals, Contacts, Companies, and Fundraising hooks.

import type { ReportingDefaultSortModel } from '../../state_manager/data_models/reporting_default_sort_model.ts'
import type { UserPreferenceReportModel } from '../../state_manager/data_models/user_preference_report_model.ts'
import type { FilterValue, SortConfig } from '../../state_manager/interfaces/deals.ts'
import { PORTFOLIO_COMPANY_ROLE_ID } from '../../state_manager/globals.ts'

export type { FilterValue, SortConfig }

/**
 * Sub-key used when a single `user_preferences_reports` row needs to hold
 * preferences for multiple tables under the same menu_item (e.g. the funds
 * menu item carries both the main Funds tablegrid AND the per-fund Investment
 * Summary table on the Fund Dashboard). Each sub-table's prefs go under
 * `data[<sub_key>]` and reuse the same `table_filters` / `table_sorting` /
 * `table_column_keys` shape.
 */
export const PREF_SUBKEY_FUND_INVESTMENT_SUMMARY = 'fund_investment_summary'
export const PREF_SUBKEY_FUND_INVESTMENT_DETAIL = 'fund_investment_detail'
export const PREF_SUBKEY_INVESTMENT_DETAIL = 'investment_detail'
export const PREF_SUBKEY_FUND_INVESTORS = 'fund_investors'
export const PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS = 'fund_group_subscriptions'

/** JSON key on a sub-preference row's `data` for the active aggregation list (ordered text keys). */
export const PREF_TABLE_AGGREGATION = 'table_aggregation'

/**
 * Resolve a saved aggregation list from a preference row. Returns `null` when
 * the user has no saved aggregation (caller should fall back to its default).
 */
export function resolveTableAggregationFromPreferenceRow(
  row: UserPreferenceReportModel | undefined,
  fallback: string[] | null = null
): string[] | null {
  const raw = row?.data?.[PREF_TABLE_AGGREGATION]
  if (raw === undefined) return fallback
  if (!Array.isArray(raw)) return fallback
  const keys = raw.filter((k): k is string => typeof k === 'string' && k.length > 0)
  return keys.length === 0 ? fallback : keys
}

export function areStringArraysEqual(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
  const aa = Array.isArray(a) ? a : []
  const bb = Array.isArray(b) ? b : []
  if (aa.length !== bb.length) return false
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
  return true
}

/**
 * Returns a synthetic preference row whose `data` is the value at
 * `parentRow.data[subKey]` — letting the existing
 * `resolveTableFiltersFromPreferenceRow` / `resolveTableSortingFromPreferenceRow`
 * / `resolveTableColumnKeysFromPreferenceRow` helpers operate on a nested
 * sub-tree without modification.
 *
 * Returns `undefined` if the sub-tree is missing or not an object.
 */
export function getSubPreferenceRow(
  parentRow: UserPreferenceReportModel | undefined,
  subKey: string
): UserPreferenceReportModel | undefined {
  const sub = parentRow?.data?.[subKey]
  if (!sub || typeof sub !== 'object' || Array.isArray(sub)) return undefined
  return { ...parentRow!, data: sub as Record<string, unknown> }
}

/** JSON key on `preferenceRow.data` for the active filter list. */
export const PREF_TABLE_FILTERS = 'table_filters'

/** JSON key on `preferenceRow.data` for sort rows, or `null` to mean “no sort”. */
export const PREF_TABLE_SORTING = 'table_sorting'

/** Ordered `field_name` keys (valueKey) for visible columns; overrides per-report `cfg.reporting_columns` when set. */
export const PREF_TABLE_COLUMN_KEYS = 'table_column_keys'

/**
 * Returns `null` when the user has no saved column layout (use reporting defaults).
 * Returns an array (possibly empty) when saved — order is significant.
 */
export function resolveTableColumnKeysFromPreferenceRow(
  row: UserPreferenceReportModel | undefined
): string[] | null {
  const raw = row?.data?.[PREF_TABLE_COLUMN_KEYS]
  if (raw === undefined) return null
  if (!Array.isArray(raw)) return null
  const keys = raw.filter((k): k is string => typeof k === 'string')
  // Treat empty list like “no saved layout” so we use the per-report `cfg.reporting_columns` (same as missing key).
  if (keys.length === 0) return null
  return keys
}

/** Companies “Role” column filter key (portfolio company). */
const FILTER_KEY_ACCOUNT_ROLES_ACCOUNT_ROLE_CHOICE_ID = 'account_roles__account_role_choice_id'

/** Default Companies table: Role = Portfolio (matches backend portfolio scope until user changes it). */
function defaultCompanyRoleFilters(): FilterValue[] {
  return [
    {
      key: FILTER_KEY_ACCOUNT_ROLES_ACCOUNT_ROLE_CHOICE_ID,
      value: [PORTFOLIO_COMPANY_ROLE_ID]
    }
  ]
}

export const DEFAULT_DEALS_TABLE_FILTERS: FilterValue[] = []
export const DEFAULT_CONTACTS_TABLE_FILTERS: FilterValue[] = []
export const DEFAULT_FUNDRAISING_TABLE_FILTERS: FilterValue[] = []
export const DEFAULT_COMPANIES_TABLE_FILTERS: FilterValue[] = defaultCompanyRoleFilters()
export const DEFAULT_FUNDS_TABLE_FILTERS: FilterValue[] = []

/**
 * Resolves the default sort for a report from the cfg-driven
 * `reporting_default_sorts` rows in the globalConfigs slice. Each row carries
 * `field_name` (the wire-form key the frontend sends as `sort.key` — what the
 * Python backend's `resolve_grid_sorting` translates to the canonical
 * `cfg.reporting_column_defaults.sort_key` before invoking the data RPC).
 *
 * Returns an empty list when the report id is unknown or no rows match —
 * caller should treat that as "no default sort" (e.g. a flat list view).
 */
export function getReportingDefaultSorting(
  reportingConfigId: string | null | undefined,
  reportingDefaultSorts: ReportingDefaultSortModel[] | undefined
): SortConfig[] {
  if (!reportingConfigId || !reportingDefaultSorts || reportingDefaultSorts.length === 0) return []
  return reportingDefaultSorts
    .filter((r) => r.reporting_config_id === reportingConfigId)
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map<SortConfig>((r) => ({
      key: r.field_name,
      direction: r.direction === 'desc' ? 'desc' : 'asc'
    }))
}

/**
 * Saved filters win when `table_filters` exists on the preference row; otherwise use `defaults`
 * (e.g. `[]` for most tables, or `defaultCompanyRoleFilters()` for Companies).
 */
export function resolveTableFiltersFromPreferenceRow(
  row: UserPreferenceReportModel | undefined,
  defaults: FilterValue[]
): FilterValue[] {
  if (!row?.data) return defaults

  const raw = row.data[PREF_TABLE_FILTERS]
  if (raw === undefined) return defaults
  if (raw === null) return []
  if (!Array.isArray(raw)) return defaults

  return raw as FilterValue[]
}

function parseSortConfig(raw: unknown): SortConfig | undefined {
  if (typeof raw !== 'object' || Array.isArray(raw) || raw === null) return undefined
  const payload = raw as Record<string, unknown>
  const { key, direction } = payload
  if (typeof key !== 'string') return undefined
  if (direction !== 'asc' && direction !== 'desc') return undefined
  return { key, direction }
}

function parseStoredSort(raw: unknown): SortConfig[] | null | undefined {
  if (raw === undefined) return undefined
  if (raw === null) return null

  // Backward compatibility: previously we stored a single object.
  const single = parseSortConfig(raw)
  if (single) return [single]

  if (!Array.isArray(raw)) return undefined
  const parsed = raw.map(parseSortConfig).filter((item): item is SortConfig => Boolean(item))
  return parsed
}

/**
 * Saved sort wins when `table_sorting` is set; missing key → `defaults`;
 * explicit `null` in storage → no sort.
 */
export function resolveTableSortingFromPreferenceRow(
  row: UserPreferenceReportModel | undefined,
  defaults: SortConfig[]
): SortConfig[] {
  if (!row?.data) return defaults

  const raw = row.data[PREF_TABLE_SORTING]
  if (raw === undefined) return defaults

  const parsed = parseStoredSort(raw)
  if (parsed === undefined) return defaults
  return parsed ?? []
}

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableSerialize(v)}`)
    return `{${entries.join(',')}}`
  }
  return JSON.stringify(value)
}

export function areFilterValuesEqual(a: FilterValue[], b: FilterValue[]): boolean {
  return stableSerialize(a) === stableSerialize(b)
}

export function areSortConfigsEqual(a: SortConfig[], b: SortConfig[]): boolean {
  return stableSerialize(a) === stableSerialize(b)
}
